import * as React from "react";

import s from "./retrospective.scss";
import { Nav } from "./components/nav";
import { Hero } from "./components/hero";
// import { Destaques } from "./components/destaques";
import { Revelacoes } from "./components/revelacoes";
import { Fluxo } from "./components/fluxo";
import { Ranking } from "./components/ranking";
import { Operacao } from "./components/operacao";
import { Curiosidades } from "./components/curiosidades";
import { Cta } from "./components/cta";
import { Footer } from "./components/footer";
import { useRetrospectiveData } from "./api";
import { Loading } from "./components/loading";
// import { PlaceRace } from "./components/place-race";
import { usePermission } from "#resources/hooks";
import { useStores } from "#stores";
import { useTranslation } from "react-i18next";
import { showWarningNotification } from "#resources/helpers/notifications";

function useScrollLock(isLocked: boolean) {
	React.useEffect(() => {
		if (isLocked) {
			const scrollY = window.scrollY;
			const originalBodyOverflow = document.body.style.overflow;
			const originalBodyPosition = document.body.style.position;
			const originalBodyWidth = document.body.style.width;
			const originalBodyTop = document.body.style.top;
			const originalHtmlOverflow = document.documentElement.style.overflow;

			document.body.style.overflow = "hidden";
			document.body.style.position = "fixed";
			document.body.style.width = "100%";
			document.body.style.top = `-${scrollY}px`;
			document.documentElement.style.overflow = "hidden";

			const preventScroll = (e: WheelEvent | TouchEvent | KeyboardEvent) => {
				if (e instanceof KeyboardEvent) {
					const allowedKeys = ["Tab", "Escape"];
					if (!allowedKeys.includes(e.key)) {
						e.preventDefault();
					}
					return;
				}
				e.preventDefault();
			};

			const preventKeyScroll = (e: KeyboardEvent) => {
				const scrollKeys = [
					"ArrowUp",
					"ArrowDown",
					"PageUp",
					"PageDown",
					"Home",
					"End",
					" ",
				];
				if (scrollKeys.includes(e.key)) {
					e.preventDefault();
				}
			};

			window.addEventListener("wheel", preventScroll, { passive: false });
			window.addEventListener("touchmove", preventScroll, { passive: false });
			window.addEventListener("keydown", preventKeyScroll);

			return () => {
				document.body.style.overflow = originalBodyOverflow;
				document.body.style.position = originalBodyPosition;
				document.body.style.width = originalBodyWidth;
				document.body.style.top = originalBodyTop;
				document.documentElement.style.overflow = originalHtmlOverflow;

				window.scrollTo(0, scrollY);

				window.removeEventListener("wheel", preventScroll);
				window.removeEventListener("touchmove", preventScroll);
				window.removeEventListener("keydown", preventKeyScroll);
			};
		}
	}, [isLocked]);
}

function useScrollDetection() {
	const [isScrolled, setIsScrolled] = React.useState(false);

	React.useEffect(() => {
		const handleScroll = () => {
			setIsScrolled(window.scrollY > 50);
		};

		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	return isScrolled;
}

interface RetrospectivePageProps {
	params?: {
		placeId?: string;
	};
}

export function RetrospectivePage({ params }: RetrospectivePageProps) {
	const isScrolled = useScrollDetection();
	const { t } = useTranslation("globalKeys");
	const { hasPermission } = usePermission();
	const { routerStore } = useStores();

	const [showLoading, setShowLoading] = React.useState(true);
	const [isLoadingLeaving, setIsLoadingLeaving] = React.useState(false);
	const [selectedPlaceIndex, setSelectedPlaceIndex] = React.useState(0);

	useScrollLock(showLoading);

	const hasOrgPermission = hasPermission("clientAreaOrganizationLevel");
	const hasPlacePermission = hasPermission("clientAreaPlaceLevel");
	const hasFinanceReportsPermission = hasPermission("seeFinanceReports");
	const hasBonusReportsPermission = hasPermission("seeBonusReports");
	const hasCashierReportsPermission = hasPermission("seeCashierReports");
	const hasClientsReportPermission = hasPermission("seeClientsReport");
	const hasRefundReportsPermission = hasPermission("seeRefundReports");
	const hasOpenedBillsReportPermission = hasPermission("seeOpenedBillsReport");

	const hasAllFinanceReportsPermission =
		hasFinanceReportsPermission &&
		hasBonusReportsPermission &&
		hasCashierReportsPermission &&
		hasRefundReportsPermission &&
		hasOpenedBillsReportPermission &&
		hasClientsReportPermission;

	const hasAnyPermission =
		hasOrgPermission || hasPlacePermission || hasAllFinanceReportsPermission;

	React.useEffect(() => {
		if (!hasAnyPermission) {
			showWarningNotification(t($ => $.withoutPermissionToViewPage));
			routerStore.push("/");
		}
	}, [hasAnyPermission, routerStore, t]);

	if (!hasAnyPermission) {
		return null;
	}

	const placeId = params?.placeId;

	const {
		data,
		multiPlaceData,
		isLoading,
		error,
		hasNoData,
		refetch,
	} = useRetrospectiveData({
		placeId,
		year: 2025,
	});

	const handleRetry = React.useCallback(() => {
		refetch();
	}, [refetch]);

	const handleIntroComplete = React.useCallback(() => {
		setIsLoadingLeaving(true);
		window.scrollTo(0, 0);
		setTimeout(() => {
			setShowLoading(false);
		}, 800);
	}, []);

	const year = 2025;

	return (
		<>
			<div className={`${s.container} retrospectiveRoot`}>
				{data && (
					<>
						<Nav isScrolled={isScrolled} />
						<Hero year={year} />
						<Operacao
							operatingTime={data.operatingTime}
							multiPlaceData={multiPlaceData}
							selectedPlaceIndex={selectedPlaceIndex}
							onPlaceIndexChange={setSelectedPlaceIndex}
						/>
						{/* <Destaques
							highlights={data.highlights}
							type={data.type}
							multiPlaceData={multiPlaceData}
						/> */}
						<Revelacoes
							revelations={data.revelations}
							multiPlaceData={multiPlaceData}
							selectedPlaceIndex={selectedPlaceIndex}
							onPlaceIndexChange={setSelectedPlaceIndex}
						/>
						<Fluxo
							monthlyEvolution={data.invoicing.monthlyEvolution}
							total={data.invoicing.total}
							averageTicket={data.invoicing.averageTicket}
							checkInsCompleted={data.highlights.checkInsCompleted}
							percentageVsLastYear={{
								people: data.invoicing.totalPercentageVsLastYear,
								ticket: data.invoicing.averageTicketPercentageVsLastYear,
								checkIns: data.highlights.checkInsCompletedPercentageVsLastYear,
							}}
							type={data.type}
							multiPlaceData={multiPlaceData}
							selectedPlaceIndex={selectedPlaceIndex}
							onPlaceIndexChange={setSelectedPlaceIndex}
						/>
						{/* {multiPlaceData && <PlaceRace multiPlaceData={multiPlaceData} />} */}
						<Ranking
							ranking={data.ranking}
							multiPlaceData={multiPlaceData}
							selectedPlaceIndex={selectedPlaceIndex}
							onPlaceIndexChange={setSelectedPlaceIndex}
							showOrganizationRanking={true}
						/>
						<Curiosidades
							curiosities={data.curiosities}
							summary={data.summary}
							type={data.type}
							operatingTime={data.operatingTime}
							qtd_litros_cervejas={data.qtd_litros_cervejas}
							multiPlaceData={multiPlaceData}
							selectedPlaceIndex={selectedPlaceIndex}
							onPlaceIndexChange={setSelectedPlaceIndex}
						/>
						<Cta year={year} />
						<Footer />
					</>
				)}
			</div>

			{showLoading && (
				<Loading
					year={2025}
					onComplete={handleIntroComplete}
					isLeaving={isLoadingLeaving}
					isLoading={isLoading}
					error={error}
					hasNoData={!isLoading && !error && hasNoData}
					onRetry={handleRetry}
				/>
			)}
		</>
	);
}
